import type { AssetsCreateAssetVersionSchema } from '@iconik-io/iconik-api/src/services/assets';
import type { FilesProxyCreateSchema } from '@iconik-io/iconik-api/src/services/files';
import {
  UPLOADER_STATUS_WAITING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import type { NewSubtitleMetadata } from '../../../components/modals/new_subtitle/types';
import {
  findAssetPathInStorage,
  selectUploadStorage,
} from '../../../panels/uploads/selectors';
import {
  getCurrentRouteParams,
  getCurrentRoutePattern,
} from '../../../router/current_route_params';
import type { AppDispatch, AppState, GetState } from '../../../store/types';
import { errorMessage } from '../../messages/thunks';
import { getAssetDirectoryPath } from '../helpers';
import { uploaderItemsAdded } from '../items_slice';
import { selectSourceVersionAssetFiles, selectUploads } from '../selectors';
import type { PickedFile } from '../types';
import {
  canClearUpload,
  isNewAssetUpload,
  isStagedUpload,
  isVersionUpload,
} from '../upload_state';
import {
  getNewAssetStartUpdate,
  toCustomFormatUpload,
  toNewAssetUpload,
  toPlaceholderUpload,
  toProxyUpload,
  toSubtitleUpload,
  toVersionUpload,
  type UploadRequest,
} from './upload_requests';
import { getUploaderService } from './uploader_service';

type QueuedFile = { file: PickedFile; request: UploadRequest };

const getRouteUploadStorageId = (state: AppState): string | undefined => {
  const { id, versionId } = getCurrentRouteParams();
  return selectUploadStorage(state, id, versionId, getCurrentRoutePattern());
};

/** The storage an upload without one of its own goes to. */
const getFallbackStorageId = (state: AppState): string | undefined =>
  state.storageMatchingReducer.filesStorageMatched?.id ||
  state.share.share.upload_storage_id ||
  undefined;

/** The storage id, if the app knows the storage it refers to. */
const getKnownStorageId = (
  state: AppState,
  storageId: string | undefined,
): string | undefined => {
  if (!storageId) {
    return undefined;
  }
  const matched = state.storageMatchingReducer.filesStorageMatched;
  const storage =
    state.entities.storages[storageId] ??
    (matched?.id === storageId ? matched : undefined);
  return storage?.id;
};

/**
 * Queues uploads and keeps what the uploads panel shows for them. The returned
 * states are in the order of the files.
 */
const queueUploads =
  (queuedFiles: QueuedFile[]) =>
  async (dispatch: AppDispatch): Promise<UploaderState[]> => {
    const service = getUploaderService();
    const states = await Promise.all(
      queuedFiles.map(({ request }) => service.queue(request)),
    );
    dispatch(
      uploaderItemsAdded(
        Object.fromEntries(
          states.map((state, index) => {
            const { icon, extension, fullpath } = queuedFiles[index].file;
            return [state.id, { icon, extension, fullpath }];
          }),
        ),
      ),
    );
    return states;
  };

/** Queues an upload and starts it straight away. */
const queueAndRunUpload =
  (file: PickedFile, request: UploadRequest) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const [state] = await dispatch(queueUploads([{ file, request }]));
    getUploaderService().run(state.id);
  };

/**
 * Lists new assets to upload. They wait for the user to start them, which is
 * when the panel settings are applied to them.
 */
export const addNewAssetUploads =
  (files: PickedFile[], collectionId?: string) =>
  (dispatch: AppDispatch, getState: GetState): Promise<UploaderState[]> => {
    const state = getState();
    const { transcodePriority, reflectDirectories } = state.uploaderReducer;
    const storageId = getRouteUploadStorageId(state);
    return dispatch(
      queueUploads(
        files.map((file) => ({
          file,
          request: toNewAssetUpload(file, {
            storageId,
            transcodePriority,
            collectionId,
            reflectDirectories,
          }),
        })),
      ),
    );
  };

/** Lists the file that fills a placeholder, to be started by the user. */
export const addPlaceholderUpload =
  (assetId: string, file: PickedFile, basePath?: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<UploaderState> => {
    const state = getState();
    const [uploadState] = await dispatch(
      queueUploads([
        {
          file,
          request: toPlaceholderUpload(file, {
            assetId,
            basePath,
            storageId: getRouteUploadStorageId(state),
            transcodePriority: state.uploaderReducer.transcodePriority,
          }),
        },
      ]),
    );
    return uploadState;
  };

/** Sends the staged uploads into a collection. */
export const setStagedUploadsCollection =
  (stateIds: string[], collectionId: string) =>
  async (_dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const service = getUploaderService();
    await Promise.all(
      stateIds.map((stateId) => {
        const upload = getState().uploaderStates[stateId];
        if (!upload || !isStagedUpload(upload)) {
          return undefined;
        }
        return service.update(stateId, {
          options: { baseCollectionId: collectionId },
        });
      }),
    );
  };

/**
 * Starts the listed uploads that wait for the user. New assets take the
 * panel settings as they are at this point.
 */
export const startUploads =
  (stateIds: string[]) =>
  async (_dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const service = getUploaderService();
    await Promise.all(
      stateIds.map(async (stateId) => {
        const state = getState();
        const upload = state.uploaderStates[stateId];
        if (!upload || !isStagedUpload(upload)) {
          return;
        }
        if (isNewAssetUpload(upload)) {
          const { uploaderReducer } = state;
          await service.update(
            stateId,
            getNewAssetStartUpdate(upload, state.uploaderItems[stateId], {
              storageId:
                upload.options.storageId || getFallbackStorageId(state),
              transcodePriority: uploaderReducer.transcodePriority,
              reflectDirectories: uploaderReducer.reflectDirectories,
              collectionPermissions: uploaderReducer.collectionPermissions,
              topLevelPermissions: uploaderReducer.topLevelPermissions,
              isShare: Boolean(state.share.share.id),
            }),
          );
        }
        service.run(stateId);
      }),
    );
  };

export const uploadVersion =
  (
    assetId: string,
    file: PickedFile,
    createVersionOptions: AssetsCreateAssetVersionSchema,
  ) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const sourceVersionId = createVersionOptions.source_version_id;
    const storageId =
      getRouteUploadStorageId(state) || getFallbackStorageId(state);
    const copy = sourceVersionId
      ? {
          sourceVersionId,
          copyMetadata: createVersionOptions.copy_metadata ?? true,
          copySegments: createVersionOptions.copy_segments
            ? (createVersionOptions.include_segment_types ?? [])
            : [],
        }
      : undefined;
    return dispatch(
      queueAndRunUpload(
        file,
        toVersionUpload(file, {
          assetId,
          storageId,
          basePath: storageId
            ? getAssetDirectoryPath(
                assetId,
                storageId,
                selectSourceVersionAssetFiles(state, assetId, sourceVersionId),
              )
            : undefined,
          transcodePriority: state.uploaderReducer.transcodePriority,
          copy,
        }),
      ),
    );
  };

export const uploadCustomFormat =
  (assetId: string, file: PickedFile, formatName: string) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const storageId = getKnownStorageId(
      state,
      getRouteUploadStorageId(state) || getFallbackStorageId(state),
    );
    return dispatch(
      queueAndRunUpload(
        file,
        toCustomFormatUpload(file, {
          assetId,
          storageId,
          basePath: storageId
            ? findAssetPathInStorage(state, assetId, undefined, storageId)
            : '',
          formatName,
          transcodePriority: state.uploaderReducer.transcodePriority,
        }),
      ),
    );
  };

type SubtitleUploadOptions = {
  formatName: string;
  createTranscription: boolean;
  deleteOldTranscriptions?: boolean;
  metadata?: NewSubtitleMetadata;
};

export const uploadSubtitles =
  (assetId: string, file: PickedFile, options: SubtitleUploadOptions) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState();
    const storageId = getKnownStorageId(state, getRouteUploadStorageId(state));
    const { versionId } = getCurrentRouteParams();
    return dispatch(
      queueAndRunUpload(
        file,
        toSubtitleUpload(file, {
          assetId,
          storageId,
          basePath: storageId
            ? findAssetPathInStorage(state, assetId, versionId, storageId)
            : '',
          transcodePriority: state.uploaderReducer.transcodePriority,
          ...options,
        }),
      ),
    );
  };

type ProxyUploadOptions = {
  assetId: string;
  file: PickedFile;
  storageId: string;
  /**
   * What only the caller knows about the proxy. The package fills in the
   * asset, storage and status of the proxy it creates.
   */
  proxy: FilesProxyCreateSchema;
};

export const uploadProxy =
  ({ assetId, file, storageId, proxy }: ProxyUploadOptions) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(
      queueAndRunUpload(
        file,
        toProxyUpload(file, {
          assetId,
          storageId,
          proxy,
          transcodePriority: getState().uploaderReducer.transcodePriority,
        }),
      ),
    );

/**
 * Takes uploads off the list. Whatever an unfinished upload created in Iconik
 * is deleted in the background.
 */
export const removeUploads = (stateIds: string[]) => (): void => {
  const service = getUploaderService();
  for (const stateId of stateIds) {
    service.remove(stateId);
  }
};

export const clearFinishedUploads =
  () =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const stateIds = selectUploads(getState())
      .filter(canClearUpload)
      .map((upload) => upload.id);
    dispatch(removeUploads(stateIds));
  };

export const cancelUploads =
  (uploads: UploaderState[]) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const canDeleteVersions =
      getState().rolesReducer.userRoles.canDeleteVersions;
    if (uploads.some(isVersionUpload) && !canDeleteVersions) {
      dispatch(
        errorMessage({
          text: 'You do not have permission to delete versions',
        }),
      );
      return;
    }
    dispatch(removeUploads(uploads.map((upload) => upload.id)));
  };

export const pauseUploads = (stateIds: string[]) => (): void => {
  const service = getUploaderService();
  for (const stateId of stateIds) {
    service.pause(stateId);
  }
};

export const resumeUploads = (stateIds: string[]) => (): void => {
  const service = getUploaderService();
  for (const stateId of stateIds) {
    service.run(stateId);
  }
};

/**
 * Sends uploads to another storage. One that is staged or still waiting in the
 * queue just changes course. One that already started cannot move, so it is
 * cancelled back to staged first and waits to be uploaded afresh.
 */
export const changeUploadStorage =
  (stateIds: string[], storageId: string) =>
  async (_dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const service = getUploaderService();
    await Promise.all(
      stateIds.map(async (stateId) => {
        const upload = getState().uploaderStates[stateId];
        if (!upload) {
          return;
        }
        const isQueued =
          isStagedUpload(upload) || upload.status === UPLOADER_STATUS_WAITING;
        if (!isQueued) {
          await service.cancel(stateId);
        }
        await service.update(stateId, { options: { storageId } });
      }),
    );
  };

export const retryUpload =
  (stateId: string, storageId?: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const upload = getState().uploaderStates[stateId];
    if (!upload) {
      return;
    }
    if (storageId && storageId !== upload.options.storageId) {
      await dispatch(changeUploadStorage([stateId], storageId));
      await dispatch(startUploads([stateId]));
      return;
    }
    getUploaderService().run(stateId);
  };

export const renameUpload =
  (stateId: string, customFileName: string) => async (): Promise<void> => {
    await getUploaderService().rename(stateId, customFileName);
  };

export const setUploadAssetTitle =
  (stateId: string, title: string) => async (): Promise<void> => {
    await getUploaderService().setAssetTitle(stateId, title);
  };
